// ============================================================================
// Registration "Membership" section: member/non-member toggle, membership-number
// masking + master-list cross-check (autofill), NGO question, and submit gating.
// ============================================================================

const MAX_DIGITS = 12
const LOCK_CLASSES = ['bg-gray-100', 'opacity-70', 'cursor-not-allowed']

// Mask raw input to X-XXXX-XX-XXX-XX as the user types (no trailing dash).
const formatMembershipNo = (value) => {
  const d = value.replace(/\D/g, '').slice(0, MAX_DIGITS)
  const groups = [d.slice(0, 1), d.slice(1, 5), d.slice(5, 7), d.slice(7, 10), d.slice(10, 12)]
  return groups.filter((g) => g.length).join('-')
}

const initMembershipForm = () => {
  const form = document.querySelector('form#new_user, form[id^="edit_user"]')
  const statusSelect = document.getElementById('user_member_status')
  if (!form || !statusSelect) return

  // Membership is locked after registration — nothing to wire on the edit/profile form.
  if (form.id !== 'new_user') return

  const noInput = document.getElementById('user_membership_no')
  const categorySelect = document.getElementById('user_membership_category')
  const sectorInput = document.getElementById('user_membership_sector')
  const memberFields = document.getElementById('membership_member_fields')
  const ngoSelect = document.getElementById('user_ngo')
  const ngoWrapper = document.getElementById('user_ngo_wrapper')
  const countrySelect = document.getElementById('user_country')
  const visaSelect = document.getElementById('user_require_visa')
  const errorEl = document.getElementById('membership_no_error')
  const submitBtn = document.getElementById('signup-submit-btn')
  const consentChecks = Array.from(document.querySelectorAll('.js-registration-consent'))
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content

  if (!noInput) return

  let membershipFound = false
  let debounceTimer

  const lock = (el) => {
    if (!el) return
    el.setAttribute('disabled', 'disabled')
    LOCK_CLASSES.forEach((c) => el.classList.add(c))
  }
  const unlock = (el) => {
    if (!el) return
    el.removeAttribute('disabled')
    LOCK_CLASSES.forEach((c) => el.classList.remove(c))
  }
  const setError = (msg) => {
    if (!errorEl) return
    errorEl.textContent = msg || ''
    errorEl.classList.toggle('hidden', !msg)
  }
  // Malaysia residents don't require a visa — force "No" whenever the country is Malaysia.
  const applyVisaRule = () => {
    if (!countrySelect || !visaSelect) return
    if (countrySelect.value === 'MY') visaSelect.value = 'false'
  }
  const clearAutofill = () => {
    membershipFound = false
    if (categorySelect) categorySelect.value = ''
    if (sectorInput) sectorInput.value = ''
  }

  const syncSubmit = () => {
    if (!submitBtn) return
    const consentOk = consentChecks.length === 0 || consentChecks.every((c) => c.checked)
    const status = statusSelect.value
    let membershipOk = false
    if (status === 'non_member') {
      membershipOk = !!ngoSelect?.value
    } else if (status === 'member') {
      membershipOk = membershipFound
    }
    submitBtn.disabled = !(consentOk && membershipOk)
  }

  const verify = async () => {
    lock(noInput) // temporarily lock while we look it up
    try {
      const res = await fetch('/api/verify_membership', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        body: JSON.stringify({ membership_no: noInput.value }),
      })
      const payload = await res.json()

      if (res.ok && payload && payload.success) {
        const data = payload.data || {}
        membershipFound = true
        setError('')
        if (categorySelect && data.membership_category) categorySelect.value = data.membership_category
        if (sectorInput && data.membership_sector_label) sectorInput.value = data.membership_sector_label
        if (countrySelect && data.country) {
          countrySelect.value = data.country
          applyVisaRule()
        }
      } else {
        clearAutofill()
        setError('Membership number not found. Please check and try again, or select Non-Member.')
      }
    } catch (e) {
      clearAutofill()
      setError('Could not verify membership right now. Please try again.')
    } finally {
      if (statusSelect.value === 'member') unlock(noInput) // re-enable for correction
      syncSubmit()
    }
  }

  const onNoInput = () => {
    noInput.value = formatMembershipNo(noInput.value)
    const digits = noInput.value.replace(/\D/g, '')
    membershipFound = false
    setError('')
    syncSubmit()
    clearTimeout(debounceTimer)
    if (digits.length === MAX_DIGITS) debounceTimer = setTimeout(verify, 200)
  }

  const showNgo = (visible) => {
    if (ngoWrapper) ngoWrapper.classList.toggle('hidden', !visible)
    if (!visible && ngoSelect) ngoSelect.value = ''
  }
  // The membership_no / category / sector trio is only relevant to members.
  const showMemberFields = (visible) => {
    if (memberFields) memberFields.classList.toggle('hidden', !visible)
  }

  const applyStatus = () => {
    const status = statusSelect.value
    setError('')
    clearAutofill()

    if (status === 'member') {
      unlock(noInput)
      lock(categorySelect) // autofilled + locked
      lock(sectorInput) // autofilled + locked
      showMemberFields(true)
      showNgo(false)
    } else if (status === 'non_member') {
      noInput.value = ''
      lock(noInput)
      lock(categorySelect)
      lock(sectorInput)
      showMemberFields(false) // hide the member-only trio
      showNgo(true) // non-members answer the NGO question
    } else {
      noInput.value = ''
      lock(noInput)
      lock(categorySelect)
      lock(sectorInput)
      showMemberFields(true)
      showNgo(false)
    }
    syncSubmit()
  }

  statusSelect.addEventListener('change', applyStatus)
  noInput.addEventListener('input', onNoInput)
  if (countrySelect) countrySelect.addEventListener('change', applyVisaRule)
  if (ngoSelect) ngoSelect.addEventListener('change', syncSubmit)
  consentChecks.forEach((c) => c.addEventListener('change', syncSubmit))

  applyStatus()
}

export default initMembershipForm
